import { useRef, useEffect } from "react";
import {
    Circle,
    FileUp,
    Grid3x3,
    Layers,
    Palette,
    Plus,
    Shapes,
    Trash2,
    Code,
} from "lucide-react";
import {
    usePolarGridCells,
    type RingPopulationRule,
    type ScalarMode,
    type ShapeMode,
} from "./PolarGridCellsContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";
import { isValidSvg } from "../../utils/svgUtils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clampInt(v: string | number, min: number, max: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n)
        ? Math.max(min, Math.min(max, Math.floor(n)))
        : min;
}

function clampFloat(v: string | number, min: number, max: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : min;
}

// ─── Scalar mode controls ─────────────────────────────────────────────────────

interface ScalarControlsProps {
    mode: ScalarMode;
    onModeChange: (v: ScalarMode) => void;
    uniformValue: number;
    onUniformChange: (v: number) => void;
    minValue: number;
    onMinChange: (v: number) => void;
    maxValue: number;
    onMaxChange: (v: number) => void;
    label: string;
    uniformLabel?: string;
    step?: number;
}

function ScalarControls({
    mode,
    onModeChange,
    uniformValue,
    onUniformChange,
    minValue,
    onMinChange,
    maxValue,
    onMaxChange,
    label,
    uniformLabel,
    step = 0.05,
}: ScalarControlsProps) {
    return (
        <>
            <div className="field-row">
                <label className="field-label">
                    {label} mode
                    <select
                        value={mode}
                        onChange={(e) =>
                            onModeChange(e.target.value as ScalarMode)
                        }
                    >
                        <option value="uniform">Uniform</option>
                        <option value="radial-in">
                            Radial in (large to small)
                        </option>
                        <option value="radial-out">
                            Radial out (small to large)
                        </option>
                        <option value="random">Random (per cell)</option>
                    </select>
                </label>
            </div>

            {mode === "uniform" && (
                <div className="field-row">
                    <label className="field-label">
                        {uniformLabel ?? label}
                        <DebouncedInput
                            type="number"
                            min={0}
                            max={1}
                            step={step}
                            value={uniformValue}
                            onChange={(v) =>
                                onUniformChange(clampFloat(v, 0, 1))
                            }
                            debounceMs={300}
                        />
                    </label>
                </div>
            )}

            {mode !== "uniform" && (
                <>
                    <div className="field-row">
                        <label className="field-label">
                            Min
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={step}
                                value={minValue}
                                onChange={(v) =>
                                    onMinChange(clampFloat(v, 0, 1))
                                }
                                debounceMs={300}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Max
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={step}
                                value={maxValue}
                                onChange={(v) =>
                                    onMaxChange(clampFloat(v, 0, 1))
                                }
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </>
            )}
        </>
    );
}

// ─── Main controls ────────────────────────────────────────────────────────────

export function PolarGridCellsControls() {
    const {
        rings,
        ringConfigs,
        innerRadiusFraction,
        cellPadding,
        canvasSize,
        shapeMode,
        uniformObjectSvgIndex,
        perRingObjectSvgIndices,
        perSectorObjectSvgIndices,
        sizeMode,
        uniformSize,
        sizeMin,
        sizeMax,
        opacityMode,
        uniformOpacity,
        opacityMin,
        opacityMax,
        fill,
        rotateWithCell,
        useCustomSvg,
        svgSymbol,
        objectSvgs,
        seed,
        setRings,
        setRingSectors,
        setRingRotationDeg,
        setRingHeightWeight,
        setRingCellHeightFactor,
        setRingPopulationRule,
        setInnerRadiusFraction,
        setCellPadding,
        setCanvasSize,
        setShapeMode,
        setUniformObjectSvgIndex,
        setPerRingObjectSvgIndex,
        setPerSectorObjectSvgIndex,
        setSizeMode,
        setUniformSize,
        setSizeMin,
        setSizeMax,
        setOpacityMode,
        setUniformOpacity,
        setOpacityMin,
        setOpacityMax,
        setFill,
        setRotateWithCell,
        setUseCustomSvg,
        setSvgSymbol,
        addObjectSvg,
        removeObjectSvg,
        setRingObjectSvgIndex,
        setSeed,
        applyPreset,
        getPresetValues,
        resetParameters,
        randomizeParameters,
    } = usePolarGridCells();

    const fillTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (fillTimeout.current) clearTimeout(fillTimeout.current);
        };
    }, []);

    const handleFillPicker = (value: string) => {
        if (fillTimeout.current) clearTimeout(fillTimeout.current);
        fillTimeout.current = setTimeout(() => setFill(value), 150);
    };

    const importObjectFiles = (files: File[] | FileList) => {
        for (const file of Array.from(files)) {
            const reader = new FileReader();
            reader.onload = () => {
                const text = typeof reader.result === "string" ? reader.result : "";
                if (!text || !isValidSvg(text)) return;
                addObjectSvg({ name: file.name, content: text });
            };
            reader.readAsText(file);
        }
    };

    // Ensure per-ring/sector shape arrays have enough entries for display
    const ringCount = Math.max(1, Math.round(rings));
    const sectorCount = Math.max(
        1,
        ...ringConfigs.map((rc) => Math.max(1, Math.round(rc.sectors))),
    );

    const getPerRingObjectIdx = (i: number): number =>
        perRingObjectSvgIndices[i] ?? uniformObjectSvgIndex;
    const getPerSectorObjectIdx = (i: number): number =>
        perSectorObjectSvgIndices[i] ?? uniformObjectSvgIndex;

    const setRingRuleType = (
        ringIdx: number,
        type: RingPopulationRule["type"],
    ) => {
        const nextRule: RingPopulationRule =
            type === "all"
                ? { type: "all" }
                : type === "randomDensity"
                  ? { type: "randomDensity", density: 0.5, seedOffset: 0 }
                  : type === "everyN"
                    ? { type: "everyN", step: 2, phase: 0 }
                    : { type: "fillNSkipN", fill: 1, skip: 1, phase: 0 };
        setRingPopulationRule(ringIdx, nextRule);
    };

    return (
        <div className="panel-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Polar Grid Cells</h2>
                    <ToolDescriptionPopover dialogLabel="Polar Grid Cells tool description">
                        <p>
                            Arranges shapes on a radial mesh of concentric rings
                            with independent per-ring sectors, rotation, height,
                            and population rules.
                        </p>
                        <p>
                            Paste custom SVG to use as the cell object instead
                            of built-in shapes.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="polar-grid-cells"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                {/* ── Object SVGs ───────────────────────────────────── */}
                <CollapsibleToolGroup
                    title="Object SVGs"
                    icon={Shapes}
                    defaultOpen={false}
                >
                    <p className="path-populate-preset-hint">Quick add preset</p>
                    <div className="path-populate-preset-chips">
                        {OBJECT_SVG_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                type="button"
                                className="generic-button path-populate-preset-chip path-populate-preset-chip--icon"
                                title={`Add ${preset.label} as object`}
                                aria-label={`Add ${preset.label} as object`}
                                onClick={() => {
                                    if (!isValidSvg(preset.svg)) return;
                                    addObjectSvg({
                                        name: `${preset.label} (preset)`,
                                        content: preset.svg,
                                    });
                                }}
                            >
                                <span
                                    className="path-populate-preset-chip-icon"
                                    aria-hidden="true"
                                    dangerouslySetInnerHTML={{ __html: preset.svg }}
                                />
                            </button>
                        ))}
                    </div>
                    <div className="path-populate-object-rows">
                        {objectSvgs.map((svg, index) => (
                            <div key={index} className="path-populate-object-row">
                                <div className="upload-controls">
                                    <button
                                        type="button"
                                        className="tool-file-btn tool-file-btn--stretch"
                                        onClick={() => {
                                            const input = document.createElement("input");
                                            input.type = "file";
                                            input.accept = ".svg,image/svg+xml";
                                            input.style.display = "none";
                                            input.addEventListener("change", (e) => {
                                                const file = (e.target as HTMLInputElement).files?.[0];
                                                if (!file) return;
                                                const reader = new FileReader();
                                                reader.onload = () => {
                                                    const text =
                                                        typeof reader.result === "string"
                                                            ? reader.result
                                                            : "";
                                                    if (!text || !isValidSvg(text)) return;
                                                    removeObjectSvg(index);
                                                    addObjectSvg({
                                                        name: file.name,
                                                        content: text,
                                                    });
                                                };
                                                reader.readAsText(file);
                                                document.body.removeChild(input);
                                            });
                                            document.body.appendChild(input);
                                            input.click();
                                        }}
                                    >
                                        <FileUp className="tool-file-btn__icon" size={18} aria-hidden />
                                        <span>{svg.name || `Object ${index + 1}`}</span>
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    className="tool-action-btn tool-action-btn--danger"
                                    onClick={() => removeObjectSvg(index)}
                                >
                                    <Trash2 className="tool-action-btn__icon" size={16} aria-hidden />
                                    <span>Remove</span>
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="tool-action-btn tool-action-btn--add"
                            onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.multiple = true;
                                input.accept = ".svg,image/svg+xml";
                                input.style.display = "none";
                                input.addEventListener("change", (e) => {
                                    importObjectFiles((e.target as HTMLInputElement).files ?? []);
                                    document.body.removeChild(input);
                                });
                                document.body.appendChild(input);
                                input.click();
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "copy";
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                importObjectFiles(e.dataTransfer.files);
                            }}
                        >
                            <Plus className="tool-action-btn__icon" size={16} aria-hidden />
                            <span>Add object SVG</span>
                        </button>
                    </div>
                </CollapsibleToolGroup>

                {/* ── Grid geometry ─────────────────────────────────── */}
                <CollapsibleToolGroup title="Radial Mesh" icon={Grid3x3}>
                    <div className="field-row">
                        <label className="field-label">
                            Rings
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={40}
                                step={1}
                                value={rings}
                                onChange={(v) => setRings(clampInt(v, 1, 40))}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <div className="field-label">
                            Ring settings
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 10,
                                    marginTop: 6,
                                    maxHeight: 320,
                                    overflowY: "auto",
                                }}
                            >
                                {Array.from({ length: ringCount }, (_, i) => {
                                    const rc = ringConfigs[i];
                                    const rule = rc?.populationRule ?? {
                                        type: "all",
                                    };
                                    return (
                                        <div
                                            key={i}
                                            style={{
                                                border: "1px solid rgba(127,127,127,0.25)",
                                                borderRadius: 6,
                                                padding: 8,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: "0.85em",
                                                    opacity: 0.8,
                                                    marginBottom: 6,
                                                }}
                                            >
                                                Ring {i + 1}
                                            </div>
                                            <div className="field-row field-row--two">
                                                <label className="field-label">
                                                    Sectors
                                                    <DebouncedInput
                                                        type="number"
                                                        min={1}
                                                        max={120}
                                                        step={1}
                                                        value={
                                                            rc?.sectors ?? 12
                                                        }
                                                        onChange={(v) =>
                                                            setRingSectors(
                                                                i,
                                                                clampInt(
                                                                    v,
                                                                    1,
                                                                    120,
                                                                ),
                                                            )
                                                        }
                                                        debounceMs={250}
                                                    />
                                                </label>
                                                <label className="field-label">
                                                    Rotation (deg)
                                                    <DebouncedInput
                                                        type="number"
                                                        min={-360}
                                                        max={360}
                                                        step={1}
                                                        value={
                                                            rc?.rotationDeg ?? 0
                                                        }
                                                        onChange={(v) =>
                                                            setRingRotationDeg(
                                                                i,
                                                                clampFloat(
                                                                    v,
                                                                    -360,
                                                                    360,
                                                                ),
                                                            )
                                                        }
                                                        debounceMs={250}
                                                    />
                                                </label>
                                            </div>
                                            <div className="field-row field-row--two">
                                                <label className="field-label">
                                                    Height weight
                                                    <DebouncedInput
                                                        type="number"
                                                        min={0.01}
                                                        max={20}
                                                        step={0.1}
                                                        value={
                                                            rc?.heightWeight ??
                                                            1
                                                        }
                                                        onChange={(v) =>
                                                            setRingHeightWeight(
                                                                i,
                                                                clampFloat(
                                                                    v,
                                                                    0.01,
                                                                    20,
                                                                ),
                                                            )
                                                        }
                                                        debounceMs={250}
                                                    />
                                                </label>
                                                <label className="field-label">
                                                    Cell height factor
                                                    <DebouncedInput
                                                        type="number"
                                                        min={0}
                                                        max={3}
                                                        step={0.05}
                                                        value={
                                                            rc?.cellHeightFactor ??
                                                            0.9
                                                        }
                                                        onChange={(v) =>
                                                            setRingCellHeightFactor(
                                                                i,
                                                                clampFloat(
                                                                    v,
                                                                    0,
                                                                    3,
                                                                ),
                                                            )
                                                        }
                                                        debounceMs={250}
                                                    />
                                                </label>
                                            </div>
                                            <div className="field-row">
                                                <label className="field-label">
                                                    Ring object SVG
                                                    <select
                                                        value={rc?.objectSvgIndex ?? -1}
                                                        onChange={(e) =>
                                                            setRingObjectSvgIndex(
                                                                i,
                                                                Number(e.target.value),
                                                            )
                                                        }
                                                    >
                                                        <option value={-1}>
                                                            Use shape mode
                                                        </option>
                                                        {objectSvgs.map((obj, objIdx) => (
                                                            <option key={`${obj.name}-${objIdx}`} value={objIdx}>
                                                                {obj.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            </div>
                                            <div className="field-row">
                                                <label className="field-label">
                                                    Population rule
                                                    <select
                                                        value={rule.type}
                                                        onChange={(e) =>
                                                            setRingRuleType(
                                                                i,
                                                                e.target
                                                                    .value as RingPopulationRule["type"],
                                                            )
                                                        }
                                                    >
                                                        <option value="all">
                                                            All
                                                        </option>
                                                        <option value="randomDensity">
                                                            Random density
                                                        </option>
                                                        <option value="everyN">
                                                            Every N
                                                        </option>
                                                        <option value="fillNSkipN">
                                                            Fill N, skip N
                                                        </option>
                                                    </select>
                                                </label>
                                            </div>
                                            {rule.type === "randomDensity" && (
                                                <div className="field-row field-row--two">
                                                    <label className="field-label">
                                                        Density (0-1)
                                                        <DebouncedInput
                                                            type="number"
                                                            min={0}
                                                            max={1}
                                                            step={0.05}
                                                            value={rule.density}
                                                            onChange={(v) =>
                                                                setRingPopulationRule(
                                                                    i,
                                                                    {
                                                                        ...rule,
                                                                        density:
                                                                            clampFloat(
                                                                                v,
                                                                                0,
                                                                                1,
                                                                            ),
                                                                    },
                                                                )
                                                            }
                                                            debounceMs={250}
                                                        />
                                                    </label>
                                                    <label className="field-label">
                                                        Seed offset
                                                        <DebouncedInput
                                                            type="number"
                                                            min={0}
                                                            max={999}
                                                            step={1}
                                                            value={
                                                                rule.seedOffset ??
                                                                0
                                                            }
                                                            onChange={(v) =>
                                                                setRingPopulationRule(
                                                                    i,
                                                                    {
                                                                        ...rule,
                                                                        seedOffset:
                                                                            clampInt(
                                                                                v,
                                                                                0,
                                                                                999,
                                                                            ),
                                                                    },
                                                                )
                                                            }
                                                            debounceMs={250}
                                                        />
                                                    </label>
                                                </div>
                                            )}
                                            {rule.type === "everyN" && (
                                                <div className="field-row field-row--two">
                                                    <label className="field-label">
                                                        Step (N)
                                                        <DebouncedInput
                                                            type="number"
                                                            min={1}
                                                            max={120}
                                                            step={1}
                                                            value={rule.step}
                                                            onChange={(v) =>
                                                                setRingPopulationRule(
                                                                    i,
                                                                    {
                                                                        ...rule,
                                                                        step: clampInt(
                                                                            v,
                                                                            1,
                                                                            120,
                                                                        ),
                                                                    },
                                                                )
                                                            }
                                                            debounceMs={250}
                                                        />
                                                    </label>
                                                    <label className="field-label">
                                                        Phase
                                                        <DebouncedInput
                                                            type="number"
                                                            min={0}
                                                            max={120}
                                                            step={1}
                                                            value={rule.phase}
                                                            onChange={(v) =>
                                                                setRingPopulationRule(
                                                                    i,
                                                                    {
                                                                        ...rule,
                                                                        phase: clampInt(
                                                                            v,
                                                                            0,
                                                                            120,
                                                                        ),
                                                                    },
                                                                )
                                                            }
                                                            debounceMs={250}
                                                        />
                                                    </label>
                                                </div>
                                            )}
                                            {rule.type === "fillNSkipN" && (
                                                <div className="field-row field-row--two">
                                                    <label className="field-label">
                                                        Fill
                                                        <DebouncedInput
                                                            type="number"
                                                            min={1}
                                                            max={120}
                                                            step={1}
                                                            value={rule.fill}
                                                            onChange={(v) =>
                                                                setRingPopulationRule(
                                                                    i,
                                                                    {
                                                                        ...rule,
                                                                        fill: clampInt(
                                                                            v,
                                                                            1,
                                                                            120,
                                                                        ),
                                                                    },
                                                                )
                                                            }
                                                            debounceMs={250}
                                                        />
                                                    </label>
                                                    <label className="field-label">
                                                        Skip
                                                        <DebouncedInput
                                                            type="number"
                                                            min={0}
                                                            max={120}
                                                            step={1}
                                                            value={rule.skip}
                                                            onChange={(v) =>
                                                                setRingPopulationRule(
                                                                    i,
                                                                    {
                                                                        ...rule,
                                                                        skip: clampInt(
                                                                            v,
                                                                            0,
                                                                            120,
                                                                        ),
                                                                    },
                                                                )
                                                            }
                                                            debounceMs={250}
                                                        />
                                                    </label>
                                                    <label className="field-label">
                                                        Phase
                                                        <DebouncedInput
                                                            type="number"
                                                            min={0}
                                                            max={120}
                                                            step={1}
                                                            value={rule.phase}
                                                            onChange={(v) =>
                                                                setRingPopulationRule(
                                                                    i,
                                                                    {
                                                                        ...rule,
                                                                        phase: clampInt(
                                                                            v,
                                                                            0,
                                                                            120,
                                                                        ),
                                                                    },
                                                                )
                                                            }
                                                            debounceMs={250}
                                                        />
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Inner radius (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={0.99}
                                step={0.05}
                                value={innerRadiusFraction}
                                onChange={(v) =>
                                    setInnerRadiusFraction(
                                        clampFloat(v, 0, 0.99),
                                    )
                                }
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Cell padding (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={0.99}
                                step={0.05}
                                value={cellPadding}
                                onChange={(v) =>
                                    setCellPadding(clampFloat(v, 0, 0.99))
                                }
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Canvas size (px)
                            <DebouncedInput
                                type="number"
                                min={200}
                                max={4000}
                                step={100}
                                value={canvasSize}
                                onChange={(v) =>
                                    setCanvasSize(clampInt(v, 200, 4000))
                                }
                                debounceMs={400}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={1}
                                value={seed}
                                onChange={(v) =>
                                    setSeed(Math.max(0, Math.round(Number(v))))
                                }
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                {/* ── Shapes ───────────────────────────────────────── */}
                <CollapsibleToolGroup title="Shapes" icon={Circle}>
                    <div className="field-row">
                        <label className="field-label">
                            Shape mode
                            <select
                                value={shapeMode}
                                onChange={(e) =>
                                    setShapeMode(e.target.value as ShapeMode)
                                }
                            >
                                <option value="uniform">
                                    Uniform (all cells)
                                </option>
                                <option value="per-ring">Per ring</option>
                                <option value="per-sector">Per sector</option>
                                <option value="random">Random</option>
                            </select>
                        </label>
                    </div>

                    {shapeMode === "uniform" && (
                        <div className="field-row">
                            <label className="field-label">
                                Object SVG
                                <select
                                    value={uniformObjectSvgIndex}
                                    onChange={(e) =>
                                        setUniformObjectSvgIndex(
                                            Number(e.target.value),
                                        )
                                    }
                                >
                                    <option value={-1}>None</option>
                                    {objectSvgs.map((obj, idx) => (
                                        <option key={`${obj.name}-${idx}`} value={idx}>
                                            {obj.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    )}

                    {shapeMode === "per-ring" && (
                        <div className="field-row">
                            <div className="field-label">
                                Object SVG per ring
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 4,
                                        marginTop: 4,
                                    }}
                                >
                                    {Array.from(
                                        { length: ringCount },
                                        (_, i) => (
                                            <label
                                                key={i}
                                                className="field-label"
                                                style={{
                                                    flexDirection: "row",
                                                    gap: 8,
                                                    alignItems: "center",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        minWidth: 52,
                                                        fontSize: "0.85em",
                                                        opacity: 0.7,
                                                    }}
                                                >
                                                    Ring {i + 1}
                                                </span>
                                                <select
                                                    value={getPerRingObjectIdx(i)}
                                                    onChange={(e) =>
                                                        setPerRingObjectSvgIndex(
                                                            i,
                                                            Number(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                >
                                                    <option value={-1}>
                                                        None
                                                    </option>
                                                    {objectSvgs.map((obj, idx) => (
                                                        <option key={`${obj.name}-${idx}`} value={idx}>
                                                            {obj.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        ),
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {shapeMode === "per-sector" && (
                        <div className="field-row">
                            <div className="field-label">
                                Object SVG per sector
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 4,
                                        marginTop: 4,
                                        maxHeight: 260,
                                        overflowY: "auto",
                                    }}
                                >
                                    {Array.from(
                                        { length: sectorCount },
                                        (_, i) => (
                                            <label
                                                key={i}
                                                className="field-label"
                                                style={{
                                                    flexDirection: "row",
                                                    gap: 8,
                                                    alignItems: "center",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        minWidth: 60,
                                                        fontSize: "0.85em",
                                                        opacity: 0.7,
                                                    }}
                                                >
                                                    Sector {i + 1}
                                                </span>
                                                <select
                                                    value={getPerSectorObjectIdx(i)}
                                                    onChange={(e) =>
                                                        setPerSectorObjectSvgIndex(
                                                            i,
                                                            Number(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                >
                                                    <option value={-1}>
                                                        None
                                                    </option>
                                                    {objectSvgs.map((obj, idx) => (
                                                        <option key={`${obj.name}-${idx}`} value={idx}>
                                                            {obj.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        ),
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {shapeMode === "random" && (
                        <p
                            style={{
                                fontSize: "0.8em",
                                opacity: 0.6,
                                margin: "4px 0",
                            }}
                        >
                            Object SVG chosen randomly per cell using the seed.
                        </p>
                    )}
                </CollapsibleToolGroup>

                {/* ── Size ─────────────────────────────────────────── */}
                <CollapsibleToolGroup title="Size" icon={Layers}>
                    <ScalarControls
                        label="Size"
                        uniformLabel="Size (0-1)"
                        mode={sizeMode}
                        onModeChange={setSizeMode}
                        uniformValue={uniformSize}
                        onUniformChange={setUniformSize}
                        minValue={sizeMin}
                        onMinChange={setSizeMin}
                        maxValue={sizeMax}
                        onMaxChange={setSizeMax}
                    />
                </CollapsibleToolGroup>

                {/* ── Opacity ───────────────────────────────────────── */}
                <CollapsibleToolGroup
                    title="Opacity"
                    icon={Layers}
                    defaultOpen={false}
                >
                    <ScalarControls
                        label="Opacity"
                        uniformLabel="Opacity (0-1)"
                        mode={opacityMode}
                        onModeChange={setOpacityMode}
                        uniformValue={uniformOpacity}
                        onUniformChange={setUniformOpacity}
                        minValue={opacityMin}
                        onMinChange={setOpacityMin}
                        maxValue={opacityMax}
                        onMaxChange={setOpacityMax}
                    />
                </CollapsibleToolGroup>

                {/* ── Style ─────────────────────────────────────────── */}
                <CollapsibleToolGroup title="Style" icon={Palette}>
                    <div className="field-row">
                        <label className="field-label">
                            Fill color
                            <input
                                type="color"
                                defaultValue={fill}
                                onChange={(e) =>
                                    handleFillPicker(e.target.value)
                                }
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={rotateWithCell}
                            onChange={setRotateWithCell}
                            label="Rotate shape with cell"
                            title="Aligns each shape to the outward radial direction"
                        />
                    </div>
                </CollapsibleToolGroup>

            </div>
        </div>
    );
}
